import React, { useState } from 'react';
import { Button, Input, SideModal, Typography } from 'design_system/src';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { extractErrorMessage } from 'models/shared/helpers/errors';
import { ApiKeyCreatedResponse } from 'models/api-keys/interfaces/api-keys.interface';
import { createApiKey } from 'models/api-keys/controllers/api-keys.controller';

interface CreateApiKeyProps {
	companyId: string;
	onClose: () => void;
	onSave: () => void;
}

function CreateApiKey({ companyId, onClose, onSave }: CreateApiKeyProps) {
	const [open, setOpen] = useState(true);
	const [loading, setLoading] = useState(false);
	const [label, setLabel] = useState('');
	const [error, setError] = useState('');
	const [createdKey, setCreatedKey] = useState<ApiKeyCreatedResponse | null>(
		null,
	);

	const handleClose = (value: boolean) => {
		setOpen(value);
		if (!value) onClose();
	};

	const handleSubmit = async () => {
		if (!label.trim()) {
			setError('Label is required');
			return;
		}

		setLoading(true);
		try {
			const result = await createApiKey(companyId, { label });
			setCreatedKey(result);
		} catch (err: unknown) {
			toasterNotification({
				msg: extractErrorMessage(err) || 'Error creating api key',
				toasterType: toasterOptions.error,
			});
		} finally {
			setLoading(false);
		}
	};

	const handleCopy = async () => {
		if (!createdKey) return;
		await navigator.clipboard.writeText(createdKey.apiKey);
		toasterNotification({
			msg: 'Copied to clipboard',
			toasterType: toasterOptions.success,
		});
	};

	const handleDone = () => {
		onSave();
	};

	return (
		<SideModal open={open} setOpen={handleClose} onClose={onClose}>
			<div style={{ padding: '24px', minWidth: '500px' }}>
				{!createdKey ? (
					<>
						<Typography
							scale="heading4"
							weight="600"
							style={{ marginBottom: '24px' }}
						>
							Create API Key
						</Typography>

						<Input
							name="label"
							type="text"
							title="Label"
							value={label}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								setLabel(e.target.value);
								if (error) setError('');
							}}
							required
						/>
						{error && (
							<Typography
								scale="small"
								weight="400"
								style={{ color: 'red', marginTop: 4 }}
							>
								{error}
							</Typography>
						)}

						<div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
							<Button
								variant="principal"
								onClick={handleSubmit}
								disabled={loading}
							>
								{loading ? 'Creating...' : 'Create'}
							</Button>
							<Button variant="outline" onClick={onClose}>
								Cancel
							</Button>
						</div>
					</>
				) : (
					<>
						<Typography
							scale="heading4"
							weight="600"
							style={{ marginBottom: '24px' }}
						>
							API Key created
						</Typography>

						<Typography
							scale="small"
							weight="400"
							style={{ marginBottom: '12px' }}
						>
							This is the only time the full key will be shown. Copy it now — it
							can&apos;t be retrieved again.
						</Typography>

						<Input
							name="apiKey"
							type="text"
							title="API Key"
							value={createdKey.apiKey}
							disabled
							onChange={() => undefined}
						/>

						<div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
							<Button variant="outline" onClick={handleCopy}>
								Copy
							</Button>
							<Button variant="principal" onClick={handleDone}>
								Done
							</Button>
						</div>
					</>
				)}
			</div>
			<div />
		</SideModal>
	);
}

export default CreateApiKey;
